import { Pin } from "lucide-react";
import { FormControlLabel, Checkbox } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DateField,
  TextField,
  NumberInput,
  SelectInput,
  DateTimeInput,
  BooleanInput,
  SimpleForm,
  Create,
  Edit,
  List,
  Datagrid,
  required,
  WithListContext,
  ReferenceField,
  TextInput,
} from "react-admin";
import { useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

const SCOPE_ID_TYPES = ["topic", "speaker", "organization"];

const scopeIdLabel = (listType) => {
  switch (listType) {
    case "topic": return "Topic ID";
    case "speaker": return "Speaker ID";
    case "organization": return "Organization ID";
    default: return "Scope ID";
  }
};

const validate = {
  // list_type_scope_id: [(_, values) => {
  //   if (values.list_type === "topic" && !values.list_type_scope_id) {
  //     return "Topic ID is required";
  //   }
  // }],
  lecture_id: [(_, values) => {
    if (values.list_type !== "series_home" && !values.lecture_id && !values.image_url) {
      return "Either Lecture or Image is required for non-Series pins";
    }
  }],
  series_id: [(_, values) => {
    if (values.list_type === "series_home" && !values.series_id && !values.image_url) {
      return "Series or Image is required for Series pins";
    }
  }],
  image_url: [(_, values) => {
    if (values.list_type !== "series_home" && !values.lecture_id && !values.image_url) {
      return "Either Lecture or Image is required for non-Series pins";
    }
  }],
  link_url: [(_, values) => {
    // always optional for now
  }],
  start_time: [required()],
  end_time: [required()],
  sticky_position: [],
};

function WithWatch({ render, empty }) {
  const record = useWatch();
  return record ? <>{render(record)}</> : empty;
}

const LIST_TYPE_CHOICES = [
  { id: "featured", name: "Featured" },
  { id: "featured_upper", name: "Featured Upper" },
  { id: "recent", name: "Recent" },
  { id: "trending", name: "Trending" },
  { id: "topic", name: "Topic" },
  { id: "speaker", name: "Speaker" },
  { id: "organization", name: "Organization" },
  { id: "clips", name: "Clips" },
  { id: "series_home", name: "Series" },
];

function EditForm() {
  const theme = useTheme();

  return (
    <SimpleForm
    // toolbar={<EditorToolbar />}
    >
      <SelectInput source="list_type" label="List Type" choices={LIST_TYPE_CHOICES} />
      <WithWatch label="Scope ID" render={record => (<NumberInput source="list_type_scope_id" label={scopeIdLabel(record.list_type)} helperText={record.list_type === "topic" ? "Leave blank to pin to all topics" : "Optional (leave empty for all)"} validate={validate.list_type_scope_id} sx={{ display: SCOPE_ID_TYPES.includes(record.list_type) ? "block" : "none" }} />)} empty={<span>Empty</span>} />
      <WithWatch label="Lecture to Pin" render={record => (<NumberInput source="lecture_id" label="Lecture to Pin" validate={validate.lecture_id} sx={{ display: record.list_type === "topic" || record.list_type === "recent" || record.list_type === "trending" || record.list_type === "featured" || record.list_type === "featured_upper" || record.list_type === "speaker" || record.list_type === "organization" || record.list_type === "clips" ? "block" : "none" }} />)} empty={<span>Empty</span>} />
      <WithWatch label="Series to Pin" render={record => (<NumberInput source="series_id" label="Series to Pin" validate={validate.series_id} sx={{ display: record.list_type === "series_home" ? "block" : "none" }} />)} empty={<span>Empty</span>} />
      <BooleanInput source="is_filler" label="Is Filler (i.e. goes at the end of the pins list only to fill available spaces)"  />
      <TextInput source="image_url" label="Custom Image URL (optional, overrides lecture thumbnail)" validate={validate.image_url} fullWidth />
      {/* Approving an image does not broaden who can discover or play the content. */}
      <SelectInput
        source="is_thumbnail_visible_to_all"
        label="Thumbnail Visibility"
        choices={[
          { id: "everyone", name: "Everyone" },
          { id: "female", name: "Females only" },
        ]}
        emptyText="Default rules"
        emptyValue=""
        format={value => value == null ? "" : value ? "everyone" : "female"}
        parse={value => value === "" ? null : value === "everyone"}
        helperText="Whether this pin's thumbnail's visibility should be overriden or fallback to default rules. Series by a female speaker require a custom thumbnail on either the pin or the series itself, because otherwise the speaker's photo is used as a fallback which can only be shown to female users no matter what."
      />
      <TextInput source="link_url" label="Custom Link URL (optional, overrides lecture page)" validate={validate.link_url}  fullWidth />
      <DateTimeInput source="start_time" label="Start Time" validate={validate.start_time} />
      <DateTimeInput source="end_time" label="End Time" validate={validate.end_time} />
      <NumberInput source="sticky_position" label="Sticky Position" validate={validate.sticky_position} />
      <p style={{ margin: "8px 0", color: theme.palette.text.secondary }}>
        Whether the pin is visibile for a given user depends on the lecture&apos;s discoverability flags or the series&apos;s female-only flag. "Thumbnail Visibility" only applies once the pin is visible (obviously), and controls whether its thumbnail shows to a given user.
      </p>
    </SimpleForm>
  );
}

function CreateView(props) {
  const [searchParams] = useSearchParams();

  const paramNames = ['list_type', 'list_type_scope_id', 'lecture_id', 'series_id', 'start_time', 'end_time', 'sticky_position'];

  const urlParams = paramNames.reduce((acc, param) => {
    const value = searchParams.get(param);
    if (value !== null) {
      if (['list_type_scope_id', 'lecture_id', 'series_id', 'sticky_position'].includes(param)) {
        acc[param] = parseInt(value, 10);
      } else {
        acc[param] = value;
      }
    }
    return acc;
  }, {});

  return (
    <Create
      {...props}
      record={({
        list_type: "recent",
        ...urlParams, // URL parameters override defaults
      })}
    >
      <EditForm />
    </Create>
  );
}

function EditView(props) {
  return (
    <Edit
      {...props}
    >
      <EditForm />
    </Edit>
  );
}


const filters = [
  <WithListContext
    alwaysOn
    render={({ setFilters, filterValues }) => {
      const isActive = filterValues["start_time.<"] && filterValues["end_time.>"];
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!isActive}
              onChange={(e) => {
                console.log("Is active: ", e.target.checked, filterValues);
                if (e.target.checked) {
                  setFilters({
                    ...filterValues,
                    "start_time.<": (new Date()).toISOString(),
                    "end_time.>": (new Date()).toISOString(),
                  });
                } else {
                  const { "start_time.<": _, "end_time.>": __, ...rest } = filterValues;
                  setFilters(rest);
                }
              }}
            />
          }
          label="Active Only?"
        />
      );
    }}
  />,
  // `list_type` filter
  <SelectInput
    source="list_type"
    label="List Type"
    choices={LIST_TYPE_CHOICES}
    alwaysOn
  />,
];

function ListView(props) {
  const theme = useTheme();

  const rowSx = (record) => {
    const now = new Date();
    const startTime = new Date(record.start_time);
    const endTime = new Date(record.end_time);

    if (startTime < now && endTime > now) {
      return {
        backgroundColor: `${theme.palette.secondary.light} !important`,
        '&:hover': {
          backgroundColor: `${theme.palette.secondary.main} !important`,
        }
      };
    }
    return {};
  };

  return (
    <List
      {...props}
      // actions={<CardListActions exporter={true} />}
      filters={filters}
      perPage={25}
      sort={{ field: "id", order: "DESC" }}
      title="Pins"
    >
      <Datagrid rowClick="edit" bulkActionButtons={false} rowSx={rowSx}>
        <TextField source="lecture_id" label="Lecture ID" />
        <ReferenceField
          source="lecture_id"
          reference="lectures"
          label="Lecture Title"
          link={false}
        >
          <TextField source="title" />
        </ReferenceField>
        <TextField source="list_type" label="List Type" />
        <DateField source="start_time" label="Start Time" showTime />
        <DateField source="end_time" label="End Time" showTime />
        <TextField source="sticky_position" label="Sticky Position" />
      </Datagrid>
    </List>
  );
}

export const Pins = {
  category: "database",
  name: "pins",
  roles: ["administrator"],
  list: ListView,
  create: CreateView,
  icon: Pin,
  options: { label: "Pins" },
  edit: EditView,
};
